/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 메인 페이지의 학교리스트 정보 다이얼로그
 * 버전 : 1.0
 * 파일명 : SchoolListSheet.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2026-02-05, 강동형 : 핸들러 닫기 로직 추가
 * ================================================================ */

import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Slide, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { BottomSheetType } from 'src/types/SchoolType';
import SchoolListDetail from './SchoolListDetail';
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useMainPageDialogStore } from 'src/stores/DialogStore';
import AIRecommendation from '../common/aiRecommendation';
import { parseSchoolData } from 'src/utils/Etc';

// Transition 컴포넌트 정의
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

const TitleContainer = styled('div')({
  display:'flex',
  justifyContent: 'center',
  alignItems:'center',
  flexDirection:'column'
})

const BottomSheetHandle = styled('div')({
  width: '68px',
  minHeight: '5px',
  borderRadius:'18px',
  backgroundColor: '#DDD',
  position: 'relative',
  top:'5px',
  margin: '0 auto',
  cursor: 'pointer'
})

const SchoolListSheet = React.memo(() => {
    const {isOpen, setField, selected} = useMainPageDialogStore()
    const [schools, setSchools] = useState<BottomSheetType | null>(null);
    const startYRef = useRef<number | null>(null);
    const [isAi,setIsAi] = useState(false)
  
    // ✅ BottomSheetHandle 드래그용
    const handleHandleStart = (e: React.TouchEvent | React.MouseEvent) => {
      const y = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
      startYRef.current = y;

      document.addEventListener('touchmove', handleHandleMove as any, { passive: false });
      document.addEventListener('mousemove', handleHandleMove as any);
      document.addEventListener('touchend', handleHandleEnd);
      document.addEventListener('mouseup', handleHandleEnd);
    };

    const handleHandleMove = (e: TouchEvent | MouseEvent) => {
      if (startYRef.current === null) return;

      const y = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const deltaY = y - startYRef.current;

      if (deltaY > 30) {        // 👇 아래로 30px 이상 드래그
        CloseDialog();
        handleHandleEnd();
      }
    };

    const handleHandleEnd = () => {
      startYRef.current = null;

      document.removeEventListener('touchmove', handleHandleMove as any);
      document.removeEventListener('mousemove', handleHandleMove as any);
      document.removeEventListener('touchend', handleHandleEnd);
      document.removeEventListener('mouseup', handleHandleEnd);
    };


    useEffect(() => {
      if (selected) {
        setSchools(parseSchoolData(selected));
      } else {
        setSchools(null); // 또는 아무 것도 하지 않음
      }
    }, [selected]);

  const CloseDialog = ()=>{
    setField('isOpen',false)
    setIsAi(false)
  }

  return (
    <>
        <Dialog 
          open={isOpen} 
          onClose={CloseDialog} 
          hideBackdrop={true}
          TransitionComponent={Transition}
            PaperProps={{
                sx: {
                position: 'fixed', 
                bottom: 0, 
                margin: 0, 
                width: '100vw',
                height: '100%',
                borderTopLeftRadius: 16, 
                borderTopRightRadius: 16,
                transition: 'height 0.5s ease-in-out',
                overflow: 'visible',
                }
            }}>
            <BottomSheetHandle 
              onClick={CloseDialog}
              onTouchStart={handleHandleStart}
              onMouseDown={handleHandleStart}
            />
            <TitleContainer>
              {isAi ? 
              <>
              {schools && 
                <AIRecommendation selectedSchool={schools} onBackClick={setIsAi} fromMain={true}/>
              }
              </>
              :(
                <>
                <DialogTitle sx={{fontSize:16, fontWeight:700, padding:'11px'}}>학교 상세 정보</DialogTitle>
                <ArrowBackIcon sx={{
                    fontSize:'24px',
                    position: 'absolute',
                    display:'flex',
                    left: 20
                    }}
                    onClick={()=>setField('isOpen',false)}/>
                </>)}
            </TitleContainer>
              {!isAi && schools && (
                <SchoolListDetail school={schools} setAiState={setIsAi} />
              )}
            </Dialog>
        </>
        
  )
})

export default SchoolListSheet